/**
 * Marks the button that started a write as busy until the write settles, so
 * every action shows a spinner without each page wiring its own pending state.
 * A request belongs to the last clicked button when it starts within a short
 * window of that click, or of the previous request that button started, which
 * keeps multi-step actions (options, then verify) busy across both calls.
 */
const WINDOW_MS = 600;

let last: { el: HTMLElement; at: number } | null = null;
const inflight = new WeakMap<HTMLElement, number>();

if (typeof document !== "undefined") {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as Element | null;
      const el = target?.closest?.<HTMLElement>("button, [role='menuitem'], label.switch");
      last = el ? { el, at: performance.now() } : null;
    },
    true,
  );
}

export function trackPending<T>(work: Promise<T>): Promise<T> {
  const owner = last;
  if (!owner || !owner.el.isConnected || performance.now() - owner.at > WINDOW_MS) return work;
  const el = owner.el;
  inflight.set(el, (inflight.get(el) ?? 0) + 1);
  el.setAttribute("aria-busy", "true");
  const done = () => {
    const left = (inflight.get(el) ?? 1) - 1;
    inflight.set(el, left);
    if (left > 0) return;
    el.removeAttribute("aria-busy");
    if (last?.el === el) last = { el, at: performance.now() };
  };
  work.then(done, done);
  return work;
}
